"use client"

import { useState, useEffect, useRef } from "react"
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from "lucide-react"

interface PDFModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  pdfUrl: string
}

export function PDFModal({ isOpen, onClose, title, pdfUrl }: PDFModalProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1.0)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const modalRef = useRef<HTMLDivElement>(null)

  // Sample PDF content - in a real app, you'd use actual PDF URLs
  const samplePDFContent = `
    <div style="padding: 40px; max-width: 800px; margin: 0 auto; font-family: Arial, sans-serif;">
      <h1 style="color: #1e40af; text-align: center; margin-bottom: 30px;">${title}</h1>
      
      <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h2 style="color: #334155;">Introduction to Planets</h2>
        <p style="color: #475569; line-height: 1.6;">
          Our solar system consists of eight planets orbiting the Sun. Each planet has unique characteristics 
          that make it fascinating to study.
        </p>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
        <div style="background: #fff7ed; padding: 15px; border-radius: 8px; border-left: 4px solid #ea580c;">
          <h3 style="color: #9a3412; margin: 0 0 10px 0;">Inner Planets</h3>
          <ul style="color: #92400e; margin: 0; padding-left: 20px;">
            <li>Mercury - Closest to Sun</li>
            <li>Venus - Hottest planet</li>
            <li>Earth - Our home</li>
            <li>Mars - The Red Planet</li>
          </ul>
        </div>
        
        <div style="background: #f0f9ff; padding: 15px; border-radius: 8px; border-left: 4px solid #0369a1;">
          <h3 style="color: #075985; margin: 0 0 10px 0;">Outer Planets</h3>
          <ul style="color: #0c4a6e; margin: 0; padding-left: 20px;">
            <li>Jupiter - Largest planet</li>
            <li>Saturn - Ringed beauty</li>
            <li>Uranus - Ice giant</li>
            <li>Neptune - Windy world</li>
          </ul>
        </div>
      </div>

      <div style="background: #f1f5f9; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #334155; margin-top: 0;">Planetary Facts</h3>
        <table style="width: 100%; border-collapse: collapse; color: #475569;">
          <thead>
            <tr style="background: #e2e8f0;">
              <th style="padding: 12px; text-align: left; border: 1px solid #cbd5e1;">Planet</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #cbd5e1;">Diameter</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #cbd5e1;">Orbit Period</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #cbd5e1;">Moons</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">Mercury</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">4,879 km</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">88 days</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">0</td>
            </tr>
            <tr style="background: #f8fafc;">
              <td style="padding: 12px; border: 1px solid #cbd5e1;">Venus</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">12,104 km</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">225 days</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">0</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">Earth</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">12,742 km</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">365 days</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">1</td>
            </tr>
            <tr style="background: #f8fafc;">
              <td style="padding: 12px; border: 1px solid #cbd5e1;">Mars</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">6,779 km</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">687 days</td>
              <td style="padding: 12px; border: 1px solid #cbd5e1;">2</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style="background: #fef7cd; padding: 20px; border-radius: 8px; border-left: 4px solid #d97706;">
        <h3 style="color: #92400e; margin-top: 0;">Fun Facts</h3>
        <ul style="color: #854d0e;">
          <li>Jupiter is so large that all other planets could fit inside it</li>
          <li>Saturn's rings are made of ice and rock particles</li>
          <li>Venus is the hottest planet despite not being closest to the Sun</li>
          <li>Mars has the largest volcano in the solar system - Olympus Mons</li>
        </ul>
      </div>

      <div style="text-align: center; margin-top: 30px; color: #64748b; font-size: 14px;">
        Page 1 of 2
      </div>
    </div>
  `

  useEffect(() => {
    if (isOpen) {
      // Simulate PDF loading
      setIsLoading(true)
      setError(null)
      const timer = setTimeout(() => {
        setIsLoading(false)
        setTotalPages(2) // Simulate a 2-page PDF
      }, 1500)

      return () => clearTimeout(timer)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5))
  }

  const handleResetZoom = () => {
    setScale(1.0)
  }

  const handleDownload = () => {
    // Simulate download
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = `${title.replace(/\s+/g, '_')}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          ref={modalRef}
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col relative z-10 transform transition-all duration-300 scale-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                <p className="text-sm text-slate-500">PDF Document</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors min-h-[44px]"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="p-2 text-slate-600 hover:bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <span className="text-sm text-slate-700 min-w-[100px] text-center">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="p-2 text-slate-600 hover:bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Next page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="p-2 text-slate-600 hover:bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              
              <span className="text-sm text-slate-700 min-w-[60px] text-center">
                {Math.round(scale * 100)}%
              </span>
              
              <button
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                className="p-2 text-slate-600 hover:bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              
              <button
                onClick={handleResetZoom}
                className="p-2 text-slate-600 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Reset zoom"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 overflow-auto bg-slate-100 p-8">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading PDF document...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-red-600">
                  <p>Failed to load PDF: {error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="bg-white shadow-lg mx-auto"
                style={{ 
                  transform: `scale(${scale})`,
                  transformOrigin: 'top center',
                  maxWidth: '800px',
                  minHeight: '500px'
                }}
              >
                <div 
                  dangerouslySetInnerHTML={{ __html: samplePDFContent }}
                  className="pdf-content"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-500">
              Use zoom controls to adjust viewing size
            </div>
            <div className="text-sm text-slate-500">
              Document ready
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}